import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useBreadcrumbs from '../../../utils/hooks/useBreadcrumbs';
import TitlePage from '../../../UI/TitlePage/TitlePage';
import Button from '../../../UI/Button/Button';
import Input from '../../../UI/Input/Input';
import TextArea from '../../../UI/TextArea/TextArea';
import Select from '../../../UI/Select/Select';
import SurveyAPI from '../../../services/api/SurveyService';
import { NotifyContext } from '../../../context/notifyContext';

interface QuestionFormData {
  question_text_fr: string;
  question_text_en: string;
  type: 'rating' | 'text' | 'select';
  display_order: number;
  is_active: boolean;
  is_required: boolean;
  min_rating?: number | null;
  max_rating?: number | null;
  max_length?: number | null;
  placeholder_fr?: string | null;
  placeholder_en?: string | null;
  options?: Array<{ value: string; label_fr: string; label_en: string }>;
}

interface Option {
  value: string;
  label_fr: string;
  label_en: string;
}

const AddOrUpdateQuestion: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<QuestionFormData>({
    question_text_fr: '',
    question_text_en: '',
    type: 'rating',
    display_order: 0,
    is_active: true,
    is_required: true,
    min_rating: 1,
    max_rating: 5,
    max_length: null,
    placeholder_fr: null,
    placeholder_en: null,
    options: undefined,
  });

  const [selectOptions, setSelectOptions] = useState<Option[]>([]);
  const [newOption, setNewOption] = useState<Option>({
    value: '',
    label_fr: '',
    label_en: '',
  });

  const notify = useContext(NotifyContext);

  useBreadcrumbs([
    { label: 'Tableau de bord', is_link: true, href: '/app/dashboard' },
    { label: 'Gestion des sondages', is_link: true, href: '/app/surveys' },
    { label: 'Questions', is_link: true, href: '/app/surveys/questions' },
    { label: isEdit ? 'Modifier' : 'Créer', is_link: false },
  ]);

  useEffect(() => {
    if (isEdit && id) {
      fetchQuestion();
    }
  }, [id, isEdit]);

  const fetchQuestion = async () => {
    try {
      setLoading(true);
      const response: any = await SurveyAPI.getQuestionById(Number(id));
      if (response && response.data && response.data.question) {
        const question = response.data.question;
        setFormData({
          question_text_fr: question.question_text_fr || '',
          question_text_en: question.question_text_en || '',
          type: question.type || 'rating',
          display_order: question.display_order || 0,
          is_active: question.is_active !== undefined ? question.is_active : true,
          is_required: question.is_required !== undefined ? question.is_required : true,
          min_rating: question.min_rating || 1,
          max_rating: question.max_rating || 5,
          max_length: question.max_length || null,
          placeholder_fr: question.placeholder_fr || null,
          placeholder_en: question.placeholder_en || null,
          options: question.options || null,
        });
        if (question.options && Array.isArray(question.options)) {
          setSelectOptions(question.options);
        }
      }
    } catch (err: any) {
      notify(err.message || 'Erreur lors du chargement de la question', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof QuestionFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddOption = () => {
    if (!newOption.value || !newOption.label_fr || !newOption.label_en) {
      notify('Veuillez remplir tous les champs de l\'option', 'error');
      return;
    }

    if (selectOptions.some((opt) => opt.value === newOption.value)) {
      notify('Cette valeur existe déjà', 'error');
      return;
    }

    setSelectOptions((prev) => [...prev, { ...newOption }]);
    setNewOption({ value: '', label_fr: '', label_en: '' });
  };

  const handleRemoveOption = (index: number) => {
    setSelectOptions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleMoveOption = (index: number, direction: 'up' | 'down') => {
    const newOptions = [...selectOptions];
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === newOptions.length - 1)
    ) {
      return;
    }
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [newOptions[index], newOptions[newIndex]] = [newOptions[newIndex], newOptions[index]];
    setSelectOptions(newOptions);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.question_text_fr || !formData.question_text_en) {
      notify('Les textes de la question (FR et EN) sont obligatoires', 'error');
      return;
    }

    if (formData.type === 'select' && selectOptions.length === 0) {
      notify('Au moins une option est requise pour les questions de type select', 'error');
      return;
    }

    if (formData.type === 'rating') {
      if (!formData.min_rating || !formData.max_rating) {
        notify('Les valeurs min et max sont requises pour les notes', 'error');
        return;
      }
      if (formData.min_rating >= formData.max_rating) {
        notify('La valeur min doit être inférieure à la valeur max', 'error');
        return;
      }
    }

    try {
      setLoading(true);

      const payload: any = {
        question_text_fr: formData.question_text_fr,
        question_text_en: formData.question_text_en,
        type: formData.type,
        display_order: formData.display_order || 0,
        is_active: formData.is_active,
        is_required: formData.is_required,
      };

      if (formData.type === 'rating') {
        payload.min_rating = formData.min_rating;
        payload.max_rating = formData.max_rating;
      }

      if (formData.type === 'text') {
        payload.max_length = formData.max_length || null;
        payload.placeholder_fr = formData.placeholder_fr || null;
        payload.placeholder_en = formData.placeholder_en || null;
      }

      if (formData.type === 'select') {
        payload.options = selectOptions;
      }

      let response: any;
      if (isEdit) {
        response = await SurveyAPI.updateQuestion(Number(id), payload);
      } else {
        response = await SurveyAPI.createQuestion(payload);
      }

      if (response && !response.error) {
        notify(isEdit ? 'Question mise à jour avec succès' : 'Question créée avec succès', 'success');
        navigate('/app/surveys/questions');
      } else {
        throw new Error(response?.message || 'Une erreur est survenue');
      }
    } catch (err: any) {
      notify(err.message || 'Erreur lors de la sauvegarde', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <TitlePage>
        {isEdit ? 'Modifier une question' : 'Créer une nouvelle question'}
      </TitlePage>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-6">
        {/* Question Text (FR) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Question (Français) *
          </label>
          <TextArea
            value={formData.question_text_fr}
            onChange={(e) => handleInputChange('question_text_fr', e.target.value)}
            placeholder="Entrez la question en français"
            rows={3}
            required
          />
        </div>

        {/* Question Text (EN) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Question (English) *
          </label>
          <TextArea
            value={formData.question_text_en}
            onChange={(e) => handleInputChange('question_text_en', e.target.value)}
            placeholder="Enter the question in English"
            rows={3}
            required
          />
        </div>

        {/* Question Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Type de question *
          </label>
          <Select
            value={formData.type}
            onChange={(e) => {
              handleInputChange('type', e.target.value);
              // Reset options when changing type
              if (e.target.value !== 'select') {
                setSelectOptions([]);
              }
            }}
            required
          >
            <option value="rating">Note (Étoiles)</option>
            <option value="text">Texte</option>
            <option value="select">Sélection</option>
          </Select>
        </div>

        {/* Rating-specific fields */}
        {formData.type === 'rating' && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Note minimum *
              </label>
              <Input
                type="number"
                value={formData.min_rating || 1}
                onChange={(e) => handleInputChange('min_rating', parseInt(e.target.value))}
                min={1}
                max={10}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Note maximum *
              </label>
              <Input
                type="number"
                value={formData.max_rating || 5}
                onChange={(e) => handleInputChange('max_rating', parseInt(e.target.value))}
                min={1}
                max={10}
                required
              />
            </div>
          </div>
        )}

        {/* Text-specific fields */}
        {formData.type === 'text' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Longueur maximum (caractères)
              </label>
              <Input
                type="number"
                value={formData.max_length || ''}
                onChange={(e) =>
                  handleInputChange('max_length', e.target.value ? parseInt(e.target.value) : null)
                }
                min={1}
                max={5000}
                placeholder="Laisser vide pour illimité"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Placeholder (Français)
              </label>
              <Input
                value={formData.placeholder_fr || ''}
                onChange={(e) => handleInputChange('placeholder_fr', e.target.value || null)}
                placeholder="Texte d'aide en français"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Placeholder (English)
              </label>
              <Input
                value={formData.placeholder_en || ''}
                onChange={(e) => handleInputChange('placeholder_en', e.target.value || null)}
                placeholder="Help text in English"
              />
            </div>
          </>
        )}

        {/* Select-specific fields */}
        {formData.type === 'select' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Options de sélection *
            </label>
            <div className="space-y-4">
              {/* Add new option */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded">
                <Input
                  placeholder="Valeur"
                  value={newOption.value}
                  onChange={(e) => setNewOption((prev) => ({ ...prev, value: e.target.value }))}
                />
                <Input
                  placeholder="Label (FR)"
                  value={newOption.label_fr}
                  onChange={(e) =>
                    setNewOption((prev) => ({ ...prev, label_fr: e.target.value }))
                  }
                />
                <Input
                  placeholder="Label (EN)"
                  value={newOption.label_en}
                  onChange={(e) =>
                    setNewOption((prev) => ({ ...prev, label_en: e.target.value }))
                  }
                />
                <div className="col-span-3">
                  <Button type="button" onClick={handleAddOption} className="bg-green-500 hover:bg-green-600 text-white">
                    Ajouter l'option
                  </Button>
                </div>
              </div>

              {/* List of options */}
              {selectOptions.map((option, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 p-4 bg-white border rounded"
                >
                  <div className="flex-1 grid grid-cols-3 gap-4">
                    <div>
                      <span className="text-xs text-gray-500">Valeur:</span>
                      <p className="font-medium">{option.value}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">FR:</span>
                      <p className="font-medium">{option.label_fr}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">EN:</span>
                      <p className="font-medium">{option.label_en}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleMoveOption(index, 'up')}
                      disabled={index === 0}
                      className="px-2 py-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMoveOption(index, 'down')}
                      disabled={index === selectOptions.length - 1}
                      className="px-2 py-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveOption(index)}
                      className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              ))}

              {selectOptions.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">
                  Aucune option. Ajoutez au moins une option.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Display Order */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Ordre d'affichage
          </label>
          <Input
            type="number"
            value={formData.display_order || 0}
            onChange={(e) => handleInputChange('display_order', parseInt(e.target.value) || 0)}
            min={0}
            placeholder="L'ordre sera assigné automatiquement si vide"
          />
        </div>

        {/* Checkboxes */}
        <div className="space-y-2">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.is_required}
              onChange={(e) => handleInputChange('is_required', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm text-gray-700">Champ obligatoire</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => handleInputChange('is_active', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm text-gray-700">Question active</span>
          </label>
        </div>

        {/* Submit buttons */}
        <div className="flex justify-end gap-4 pt-4 border-t">
          <Button
            type="button"
            onClick={() => navigate('/app/surveys/questions')}
            className="bg-gray-200 hover:bg-gray-300"
          >
            Annuler
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="bg-blue-500 hover:bg-blue-600 text-white disabled:opacity-50"
          >
            {loading ? 'Enregistrement...' : isEdit ? 'Mettre à jour' : 'Créer'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddOrUpdateQuestion;
